import React from "react";
import * as ExcelJS from "exceljs";
import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import getParticipantesFinalesArt from '../../services/participant/participantesFinalesArtService';

import logoART from "../../assets/logo_participantesF/ART.png";

const COLORS = {
  AZUL_OSCURO: "FF0B2C5D",
  AZUL_MARINO: "FF082444",
  AZUL_CLARO: "FFD9E3F0",
  AZUL_CLARO_2: "FFE8EFF8",
  MARRON: "FF7A4A2E",
  MARRON_CLARO: "FFF2E2D5",
  CAFE_OSCURO: "FF5C3A1E",
  CAFE_CLARO: "FFEDE3D8",
  VERDE_OSCURO: "FF1F6B3A",
  VERDE_CLARO: "FFE3F2E5",
  GRIS: "FF6E6E6E",
  GRIS_CLARO: "FFEAEAEA",
  AMARILLO_CLARO: "FFFFF2CC",
  BLANCO: "FFFFFFFF",
};
const imageToBase64 = async (imagePath) => {
  const response = await fetch(imagePath);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve(reader.result.replace(/^data:image\/(png|jpg|jpeg);base64,/, ""));
    reader.readAsDataURL(blob);
  });
};
const withSoftBorder = (cell) => {
  cell.border = {
    top: { style: "thin", color: { argb: COLORS.BLANCO } },
    left: { style: "thin", color: { argb: COLORS.BLANCO } },
    right: { style: "thin", color: { argb: COLORS.BLANCO } },
    bottom: { style: "thin", color: { argb: COLORS.BLANCO } },
  };
};
const createHeader = (sheet, range, text, bgColor) => {
  sheet.mergeCells(range);
  const cell = sheet.getCell(range.split(":")[0]);
  cell.value = text.toUpperCase();
  cell.font = { size: 10, color: { argb: COLORS.BLANCO } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
  withSoftBorder(cell);
};
const createSubHeader = (sheet, cellAddr, text, bgColor) => {
  const cell = sheet.getCell(cellAddr);
  cell.value = text ? text.toUpperCase() : "";
  cell.font = { size: 10, color: { argb: COLORS.AZUL_OSCURO } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
  withSoftBorder(cell);
};

const ExportExcelBodecom = ({ proyectoCodigo }) => {
  const exportExcel = async () => {

    const response = await getParticipantesFinalesArt(proyectoCodigo);

    if (response.status !== 200 || !response.data?.length) {
      message.warning("No hay participantes para exportar");
      return;
    }

    const participantes = response.data;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("FORMATO ART", {
      views: [{ showGridLines: false, zoomScale: 30 }],
    });

    for (let col = 1; col <= 76; col++) {
      sheet.getColumn(col).width = 13;
    }

    sheet.mergeCells("F1:AE1");
    const t = sheet.getCell("F1");
    t.value = "ESTRUCTURACIÓN Y EJECUCIÓN DE PROYECTOS";
    t.font = { bold: true, size: 13, color: { argb: COLORS.AZUL_OSCURO } };
    t.alignment = { vertical: "middle", horizontal: "center" };
    sheet.getRow(1).height = 28;
    sheet.mergeCells("A2:E2");
    sheet.getRow(2).height = 80;

    const logoBase64 = await imageToBase64(logoART);
    const imageId = workbook.addImage({
      base64: logoBase64,
      extension: "png",
    });

    sheet.addImage(imageId, {
      tl: { col: 0, row: 1 },
      br: { col: 5, row: 2 },
    });

    sheet.mergeCells("F2:O2");
    sheet.getCell("F2").value =
      "DIRECCIÓN DE ESTRUCTURACIÓN Y EJECUCIÓN DE PROYECTOS/SUBDIRECCIÓN DE DESARROLLO ECONÓMICO";

    sheet.mergeCells("P2:T2");
    sheet.getCell("P2").value = "Código: FM-EEP-60";

    sheet.mergeCells("U2:W2");
    sheet.getCell("U2").value = "Versión: 01";

    sheet.mergeCells("X2:AE2");
    sheet.getCell("X2").value = "Fecha de publicación: 17-04-2023";

    ["F2", "P2", "U2", "X2"].forEach((c) => {
      sheet.getCell(c).font = { size: 10, color: { argb: COLORS.AZUL_OSCURO } };
      sheet.getCell(c).alignment = { vertical: "middle", horizontal: "center" };
    });

    sheet.getRow(4).height = 45;
    createHeader(sheet, "A4:A4", "No.", COLORS.AZUL_OSCURO);
    createHeader(sheet, "B4:M4", "DATOS BÁSICOS", COLORS.AZUL_OSCURO);
    createHeader(sheet, "N4:R4", "NIVEL ESCOLARIDAD (MARQUE X)", COLORS.MARRON);
    createHeader(sheet, "S4:AE4", "POBLACIÓN (MARQUE X)", COLORS.VERDE_OSCURO);
    createHeader(sheet, "AF4:AM4", "INFORMACIÓN FAMILIAR", COLORS.AZUL_MARINO);
    createHeader(sheet, "AN4:BI4", "INFORMACIÓN DEL PROYECTO VINCULADO", COLORS.CAFE_OSCURO);
    createHeader(sheet, "BJ4:BM4", "DATOS DE CONTACTO DEL BENEFICIARIO", COLORS.GRIS);
    createHeader(sheet, "BN4:BO4", "CONECTIVIDAD", COLORS.AZUL_OSCURO);
    createHeader(sheet, "BP4:BS4", "INFORMACIÓN ORGANIZACIONAL", COLORS.CAFE_OSCURO);
    createHeader(sheet, "BT4:BX4", "VALIDACIÓN INTERNA ART", COLORS.AZUL_OSCURO);
    sheet.getRow(5).height = 70;
    createSubHeader(sheet, "A5", "", COLORS.AZUL_OSCURO);
    [
      "PRIMER NOMBRE", "SEGUNDO NOMBRE", "PRIMER APELLIDO", "SEGUNDO APELLIDO",
      "TIPO DE IDENTIFICACIÓN", "NO. DE IDENTIFICACIÓN", "SEXO", "FECHA DE NACIMIENTO",
      "", "¿SABE LEER Y ESCRIBIR? SI/NO",
      "INGRESOS MENSUALES (PESOS M/CTE)", "LÍNEA ECONÓMICA PRINCIPAL",
    ].forEach((t, i) =>
      createSubHeader(sheet, sheet.getCell(5, 2 + i).address, t, COLORS.AZUL_CLARO)
    );
    ["NINGUNA", "PRIMARIA", "SECUNDARIA", "TÉCNICA", "PROFESIONAL"]
      .forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(5, 14 + i).address, t, COLORS.MARRON_CLARO)
      );
    [
      "CAMPESINO", "COMUNIDAD INDÍGENA", "COMUNIDAD NARP", "COMUNIDAD ROM",
      "VÍCTIMA", "DISCAPACIDAD", "LGBT+", "MUJER RURAL", "JOVEN RURAL",
      "ADULTO MAYOR", "REINCORPORADO", "OTRO", "¿CUÁL?",
    ].forEach((t, i) =>
      createSubHeader(sheet, sheet.getCell(5, 19 + i).address, t, COLORS.VERDE_CLARO)
    );
    [
      "ES CABEZA DE FAMILIA (SI/NO)", "NO. PERSONAS A CARGO",
      "TOTAL NÚCLEO FAMILIAR", "ESTADO CIVIL",
      "NOMBRE COMPLETO CÓNYUGE", "TIPO ID DEL CÓNYUGE",
      "NO. ID DEL CÓNYUGE", "NÚMERO DE CONTACTO DEL CÓNYUGE",
    ].forEach((t, i) =>
      createSubHeader(sheet, sheet.getCell(5, 32 + i).address, t, COLORS.AZUL_CLARO_2)
    );
    [
      "CÓDIGO PROYECTO ART", "NOMBRE DEL PROYECTO", "SUBREGIÓN PDET", "DEPARTAMENTO", "MUNICIPIO",
      "TIPO ZONA INFLUENCIA DIRECTA", "NOMBRE ZONA INFLUENCIA", "CÓDIGO SHAPE VEREDAS",
      "PREDIO / FINCA", "TENENCIA", "LATITUD", "LONGITUD",
      "NO. HECTÁREAS PROYECTO", "LÍNEA PRODUCTIVA",
      "VARIEDAD", "NO. HECTÁREAS", "PRODUCCIÓN ANUAL",
      "DISTANCIA (KM)", "TIEMPO (MIN)", "MEDIO TRANSPORTE",
      "¿VIVE EN EL PROYECTO?", "LUGAR DE VIVIENDA",
    ].forEach((t, i) =>
      createSubHeader(sheet, sheet.getCell(5, 40 + i).address, t, COLORS.CAFE_CLARO)
    );
    ["DIRECCIÓN DE CORRESPONDENCIA", "TELÉFONO CONTACTO 1", "TELÉFONO CONTACTO 2", "CORREO ELECTRÓNICO"]
      .forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(5, 62 + i).address, t, COLORS.GRIS_CLARO)
      );
    ["TELEFÓNICA SI/NO", "INTERNET SI/NO"]
      .forEach((t, i) =>
        createSubHeader(sheet, sheet.getCell(5, 66 + i).address, t, COLORS.AZUL_CLARO_2)
      );
    [
      "¿VINCULADO A ORGANIZACIÓN?", "TIPO DE ORGANIZACIÓN",
      "NOMBRE DE LA ORGANIZACIÓN", "ORGANIZACIÓN DE MUJERES SI/NO",
    ].forEach((t, i) =>
      createSubHeader(sheet, sheet.getCell(5, 68 + i).address, t, COLORS.AMARILLO_CLARO)
    );
    [
      "VALIDADO REGISTRADURÍA", "VALIDADO PROCURADURÍA", "VALIDADO POLICÍA",
      "ESTADO DEL PROYECTO", "OBSERVACIÓN ESTADO",
    ].forEach((t, i) =>
      createSubHeader(sheet, sheet.getCell(5, 72 + i).address, t, COLORS.AZUL_CLARO_2)
    );
    let startRow = 6;

    participantes.forEach((p, index) => {
      const row = sheet.getRow(startRow + index);
      row.getCell(1).value = p.no;
      row.getCell(2).value = p.nombre_participante?.split(" ")[0] || "";
      row.getCell(3).value = p.nombre_participante?.split(" ")[1] || "";
      row.getCell(4).value = p.nombre_participante?.split(" ")[2] || "";
      row.getCell(5).value = p.nombre_participante?.split(" ")[3] || "";
      row.getCell(6).value = p.tipo_identificacion;
      row.getCell(7).value = p.numero_identificacion;
      row.getCell(8).value = p.sexo;
      row.getCell(9).value = p.fecha_nacimiento ? p.fecha_nacimiento.split("T")[0] : "";
      row.getCell(11).value = p.sabe_leer_escribir;
      row.getCell(12).value = p.ingresos_mensuales;
      row.getCell(13).value = p.linea_economica_principal;
      row.getCell(14).value = p.ninguna;
      row.getCell(15).value = p.primaria;
      row.getCell(16).value = p.secundaria;
      row.getCell(17).value = p.tecnica;
      row.getCell(18).value = p.profesional;
      row.getCell(19).value = p.Campesino;
      row.getCell(20).value = p.Comunidad_Indigena;
      row.getCell(21).value = p.Comunidad_NARP;
      row.getCell(22).value = p.Comunidad_ROM;
      row.getCell(23).value = p.Victima;
      row.getCell(24).value = p.Discapacidad;
      row.getCell(25).value = p.LGBT_plus;
      row.getCell(26).value = p.Mujer_Rural;
      row.getCell(27).value = p.Joven_Rural;
      row.getCell(28).value = p.Adulto_Mayor;
      row.getCell(29).value = p.Reincorporado;
      row.getCell(30).value = p.Otro;
      row.getCell(32).value = p.cabeza_familia;
      row.getCell(33).value = p.num_personas_cargo;
      row.getCell(34).value = p.total_nucleo_familiar;
      row.getCell(35).value = p.estado_civil;
      row.getCell(36).value = p.nombre_conyuge;
      row.getCell(37).value = p.tipo_id_conyuge;
      row.getCell(38).value = p.identificacion_conyuge;
      row.getCell(39).value = p.numero_contacto_conyuge;
      row.getCell(40).value = p.codigo_proyecto;
      row.getCell(41).value = p.nombre_proyecto;
      row.getCell(42).value = p.nombre_subregion_pdet;
      row.getCell(43).value = p.departamento;
      row.getCell(44).value = p.municipio;
      row.getCell(45).value = p.tipo_zona_influencia;
      row.getCell(46).value = p.zona_influencia;
      row.getCell(47).value = p.codigo_shape;
      row.getCell(48).value = p.nombre_predio;
      row.getCell(49).value = p.relacion_tenencia_predio;
      row.getCell(50).value = p.latitud;
      row.getCell(51).value = p.longitud;
      row.getCell(52).value = p.num_hectarias_proyecto;
      row.getCell(53).value = p.linea_productiva;
      row.getCell(54).value = p.variedad_establecida;
      row.getCell(55).value = p.num_hectareas;
      row.getCell(56).value = p.produccion_anual;
      row.getCell(57).value = p.distancia_cabecera_municipal;
      row.getCell(58).value = p.tiempo_cabecera_municipal;
      row.getCell(59).value = p.medio_trasporte;
      row.getCell(60).value = p.vive_proyecto;
      row.getCell(61).value = p.lugar_vivienda;
      row.getCell(62).value = p.direccion_correspondencia;
      row.getCell(63).value = p.numero_telefonico_1;
      row.getCell(64).value = p.numero_telefonico_2;
      row.getCell(65).value = p.correo_electronico;
      row.getCell(66).value = p.telefonica;
      row.getCell(67).value = p.internet;
      row.getCell(68).value = p.vinculado_org;
      row.getCell(69).value = p.tipo_org;
      row.getCell(70).value = p.nombre_org;
      row.getCell(71).value = p.org_mujeres;
      row.getCell(75).value = p.estado_proyecto;
      row.getCell(76).value = p.observacion_estado;
      row.eachCell((cell) => withSoftBorder(cell));
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Formato_ART_Completo.xlsx");
  };

  return (
    <Button type="primary" icon={<DownloadOutlined />} onClick={exportExcel}>
      Exportar Participantes Finales
    </Button>
  );
};

export default ExportExcelBodecom;
